import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  DateUtility,
  getTimeDifference,
  manageErrorLogs,
} from "../helpers/commonFunction";
import { handleOfflineDataSyncing } from "../helpers/offlineAPIs";
import {
  DB,
  insertEntryInDB,
  getAllEntriesFromDB,
  reqToExportTableData,
  reqToImportTableData,
} from "../helpers/db";
import {
  reqToUserLogout,
  reqToForceUserLogout,
  reqToUserLogoutInLocal,
} from "../store/slice/userSlice";
import {
  reqToUserLogoutProcess,
  reqToInitialSyncProcess,
} from "../store/slice/offlineSlice";
import { SYNC_TYPE } from "../helpers/constants";

const useFetchAndSyncData = (sync = true) => {
  const dispatch = useDispatch();
  const [syncType, setSyncType] = useState("");
  const [syncMessage, setSyncMessage] = useState("");
  const [offlineModal, setOfflineModal] = useState(false);
  const handleOfflineModal = () => {
    setOfflineModal(!offlineModal);
  };
  const [confirmationModal, setConfirmationModal] = useState(false);
  const handleConfirmModal = () => {
    setConfirmationModal(!confirmationModal);
  };

  const uploadAndFetchData = async (type, callBack) => {
    setSyncType(type);
    if (type === "logs") {
      const [userData, systemLogs] = await Promise.all([
        getAllEntriesFromDB("userData"),
        getAllEntriesFromDB("systemLogs"),
      ]);
      const newUser = userData && userData?.length > 0 && userData[0];
      const logContent = systemLogs
        .map((error) => {
          let logEntry = "";
          Object.keys(error).forEach((key) => {
            logEntry += `${key}: ${
              typeof error[key] === "object"
                ? JSON.stringify(error[key], null, 2)
                : error[key]
            }\n`;
          });
          return logEntry + "\n";
        })
        .join("\n");
      const blob = new Blob([logContent], { type: "text/plain" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${newUser?.lastname}_${
        newUser?.firstname
      }_${DateUtility.mmddyydashFormat(new Date())}_system.log`;
      link.click();
      URL.revokeObjectURL(link.href);
    } else if (type === "export") {
      setSyncType("");
      const tableNames = [
        "addedRestaurantDetails",
        "addedRestaurantTaskNotes",
        "userData",
      ];
      await reqToExportTableData(tableNames, dispatch);
    } else if (type === "import") {
      setSyncType("");
      await reqToImportTableData(callBack, dispatch);
    } else if (window.navigator.onLine) {
      setConfirmationModal(false);
      const [lastAPICallTime] = await Promise.all([
        getAllEntriesFromDB("lastAPICallTime"),
      ]);

      const waitForSyncAvailability = async () => {
        let lastAPICall = await getAllEntriesFromDB("lastAPICallTime");
        lastAPICall = lastAPICall?.length > 0 ? lastAPICall[0] : null;
        while (lastAPICall && lastAPICall.call === 1) {
          await new Promise((resolve) =>
            setTimeout(async () => {
              const { minutes } = getTimeDifference(
                new Date(),
                lastAPICall?.time,
              );
              if (minutes >= 1) {
                await updateLastAPICallTime("end", lastAPICall?.id);
                resolve();
              } else {
                resolve();
              }
            }, 1000),
          );
          lastAPICall = await getAllEntriesFromDB("lastAPICallTime");
          lastAPICall = lastAPICall?.length > 0 ? lastAPICall[0] : null;
        }
        return lastAPICall;
      };

      const syncAction = async (id, isNew = false) => {
        try {
          if (!isNew) {
            await updateLastAPICallTime("start", id);
          }
          const isSync = sync || type === SYNC_TYPE.SYNC;
          await handleOfflineDataSyncing(
            dispatch,
            isSync,
            type ? type : SYNC_TYPE.SYNC,
          );
          await updateLastAPICallTime("end", id);
          if (sync) {
            setSyncType("");
            setSyncMessage("");
            setConfirmationModal(false);
          }
        } catch (err) {
          manageErrorLogs(err);
          await updateLastAPICallTime("end", id);
          if (sync && err?.name !== "CanceledError") {
            setSyncMessage(err.message || "An error occurred during sync");
            setConfirmationModal(true);
          }
        }
      };

      if (
        type === SYNC_TYPE.UPLOAD ||
        type === SYNC_TYPE.LOGOUT ||
        type === SYNC_TYPE.ISNMONTH ||
        type === SYNC_TYPE.FINALUPLOAD ||
        type === SYNC_TYPE.BROWSERLOGOUT
      ) {
        try {
          if (sync) dispatch(reqToInitialSyncProcess(true));
          await waitForSyncAvailability();
          await handleOfflineDataSyncing(
            dispatch,
            sync,
            type ? type : SYNC_TYPE.SYNC,
          );
          setSyncType("");
          setSyncMessage("");
          setConfirmationModal(false);
          if (type === SYNC_TYPE.UPLOAD) {
            return Promise.resolve(true);
          } else if (
            type === SYNC_TYPE.ISNMONTH ||
            type === SYNC_TYPE.FINALUPLOAD
          ) {
            callBack && (await callBack());
          } else if (
            type === SYNC_TYPE.LOGOUT ||
            type === SYNC_TYPE.BROWSERLOGOUT
          ) {
            callBack && (await callBack());
            handleUserLogout();
          }
        } catch (err) {
          manageErrorLogs(err);
          if (err?.name !== "CanceledError") {
            setSyncMessage(err.message || "An error occurred during sync");
            setConfirmationModal(true);
          }
        }
      } else if (lastAPICallTime && lastAPICallTime?.length > 0) {
        const lastAPICall = lastAPICallTime[0];
        if (type === SYNC_TYPE.SYNC) {
          if (sync) dispatch(reqToInitialSyncProcess(true));
          await waitForSyncAvailability();
          syncAction(lastAPICall?.id);
        } else if (lastAPICall && !lastAPICall.call && lastAPICall?.id) {
          syncAction(lastAPICall?.id);
        } else {
          if (sync) return;
          const { minutes } = getTimeDifference(new Date(), lastAPICall?.time);
          if (minutes >= 2) {
            syncAction(lastAPICall?.id);
          }
        }
      } else {
        const insertID = await insertEntryInDB("lastAPICallTime", {
          call: 1,
          time: new Date(),
        });
        syncAction(insertID, true);
      }
    } else if (type === SYNC_TYPE.LOGOUT) {
      setSyncType("");
      dispatch(reqToUserLogoutInLocal());
    } else {
      setOfflineModal(true);
    }
  };

  const updateLastAPICallTime = async (type, id) => {
    if (type === "start" && id) {
      await DB.lastAPICallTime.put({ id, call: 1, time: new Date() });
    } else if (type === "end" && id) {
      await DB.lastAPICallTime.put({ id, call: 0, time: new Date() });
    }
  };

  const handleUserLogout = () => {
    dispatch(
      reqToUserLogout({
        data: "",
        onSuccess: () => {
          dispatch(reqToUserLogoutProcess(true));
        },
        onFailure: async () => {
          if (window.navigator.onLine) {
            dispatch(reqToUserLogoutProcess(true));
            dispatch(reqToForceUserLogout());
          }
        },
      }),
    );
  };

  return {
    syncType,
    setSyncType,
    syncMessage,
    setSyncMessage,
    offlineModal,
    setOfflineModal,
    confirmationModal,
    setConfirmationModal,
    handleOfflineModal,
    handleConfirmModal,
    uploadAndFetchData,
  };
};

export default useFetchAndSyncData;
